import React, { useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import useAppStore from '../../store';

function CommandPrompt() {
  const logs = useAppStore((state) => state.logs);
  const agents = useAppStore((state) => state.agents);
  const appendLogs = useAppStore((state) => state.appendLogs);
  const addAuditEntry = useAppStore((state) => state.addAuditEntry);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const execute = (raw) => {
    const input = raw.trim();
    if (!input) return;

    appendLogs([{
      id: `cmd-${Date.now()}`,
      agent: 'operator',
      level: 'info',
      message: `> ${input}`,
      timestamp: Date.now()
    }]);

    const [command, ...args] = input.split(/\s+/);
    let reply;

    switch (command.toLowerCase()) {
      case 'help':
        reply = 'Commands: help · status · agents · pause <agent> · resume <agent> · kill <agent> · clear';
        break;
      case 'status':
        reply = `Orchestrator healthy · ${agents.filter((a) => a.status === 'running').length} running · ${agents.filter((a) => a.status === 'autonomous').length} autonomous · queue depth 3`;
        break;
      case 'agents':
        reply = agents.map((agent) => `${agent.name}[${agent.status}]`).join(' · ');
        break;
      case 'pause':
      case 'resume':
      case 'kill': {
        const agent = agents.find((candidate) => (
          candidate.name.toLowerCase() === (args[0] ?? '').toLowerCase()
        ));
        reply = agent
          ? `${agent.name} ${command === 'kill' ? 'aborted and forced idle' : `${command}d`}. Audit entry recorded.`
          : `Unknown agent "${args[0] ?? ''}" — try: ${agents.map((a) => a.name.toLowerCase()).join(', ')}`;
        if (agent) {
          addAuditEntry({ action: `agent.${command}`, detail: `${agent.name} via command prompt` });
        }
        break;
      }
      case 'clear':
        reply = '';
        break;
      default:
        reply = `Unknown command "${command}". Type "help" for the command list.`;
    }

    if (command.toLowerCase() === 'clear') {
      useAppStore.getState().clearLogs();
    } else if (reply) {
      window.setTimeout(() => {
        appendLogs([{
          id: `out-${Date.now()}`,
          agent: 'agent-0',
          level: 'info',
          message: reply,
          timestamp: Date.now()
        }]);
      }, 120);
    }
  };

  const handleKeyDown = (domEvent) => {
    if (domEvent.key === 'Enter') {
      const value = domEvent.target.value;
      if (value.trim()) {
        const next = [value, ...history].slice(0, 100);
        setHistory(next);
        setHistoryIndex(-1);
      }
      execute(value);
      domEvent.target.value = '';
      return;
    }

    if (domEvent.key === 'ArrowUp') {
      domEvent.preventDefault();
      const nextIndex = Math.min(historyIndex + 1, history.length - 1);
      if (nextIndex >= 0) {
        setHistoryIndex(nextIndex);
        domEvent.target.value = history[nextIndex];
      }
      return;
    }

    if (domEvent.key === 'ArrowDown') {
      domEvent.preventDefault();
      const nextIndex = historyIndex - 1;
      if (nextIndex < 0) {
        setHistoryIndex(-1);
        domEvent.target.value = '';
      } else {
        setHistoryIndex(nextIndex);
        domEvent.target.value = history[nextIndex];
      }
    }
  };

  return (
    <div className="command-prompt">
      <span className="command-prompt__sigil"><FiIcons.FiChevronRight /></span>
      <input
        aria-label="Console command"
        autoComplete="off"
        onKeyDown={handleKeyDown}
        placeholder="Type a command — help for options, ↑/↓ for history"
        spellCheck="false"
        type="text"
      />
    </div>
  );
}

export default CommandPrompt;
